<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import FileSelector from '@/components/FileSelector.vue';
import { getVaultFileMetadata } from '@/api';
import { useGaiaStore } from '@/stores/gaiaStore';
import { useVaultStore } from '@/stores/vaultStore';
import { extractErrorDetail } from '@/utils/errorHandler';
import { useGaiaToast } from '@/composables/useGaiaToast';

const props = defineProps<{
  show: boolean;
}>();

const emit = defineEmits<{
  (event: 'update:show', value: boolean): void;
  // Resolved input revision uids, one run is started per uid by the parent.
  (event: 'confirm', revisionUids: string[]): void;
}>();

const message = useGaiaToast();
const gaiaStore = useGaiaStore();
const vaultStore = useVaultStore();

const selectedFileKeys = ref<string[]>([]);
const activeTab = ref<string>('upload');
const pendingUploadCount = ref<number>(0);
const preparing = ref<boolean>(false);

const fileSelectorRef = ref<InstanceType<typeof FileSelector>>();

const showModal = computed({
  get: () => props.show,
  set: value => {
    emit('update:show', value);
  },
});

// File-vault and GAIA tabs need a wide modal; the upload tab is comfortable narrow.
const modalStyle = computed(() =>
  activeTab.value === 'upload'
    ? 'width: 50vw; max-width: 600px; height: 90vh'
    : 'width: 90vw; max-width: 1000px; height: 95vh'
);

const selectedInputCount = computed(() => selectedFileKeys.value.length + pendingUploadCount.value);

const canStart = computed(() => !preparing.value && selectedInputCount.value > 0);

function handleUploadSelectionChange(count: number): void {
  pendingUploadCount.value = count;
}

function resetState(): void {
  selectedFileKeys.value = [];
  pendingUploadCount.value = 0;
  preparing.value = false;
}

function close(): void {
  showModal.value = false;
}

/** Resolve a vault file uid to its current revision uid (runs accept a revision uid). */
async function resolveRevisionUid(fileUid: string): Promise<string | null> {
  const response = await getVaultFileMetadata({ path: { file_uid: fileUid } });
  return response.data?.current_revision?.uid ?? null;
}

async function start(): Promise<void> {
  if (!canStart.value) {
    message.error('Select at least one file to run.');
    return;
  }

  preparing.value = true;
  try {
    // Upload any pending files from the upload tab; they land as vault files.
    const fileSelector = fileSelectorRef.value;
    const hasUploadFiles = (fileSelector?.getSelectedFiles()?.length ?? 0) > 0;
    const uploadedUids =
      hasUploadFiles && fileSelector ? await fileSelector.uploadSelectedFiles() : [];

    const { fileUids, gaiaUids } = fileSelectorRef.value?.getAllSelectedUids() ?? {
      fileUids: [],
      gaiaUids: [],
    };

    // GAIA files must be imported into the vault before they can be run.
    const importedUids: string[] = [];
    for (const gaiaUid of gaiaUids) {
      try {
        const imported = await gaiaStore.importGaiaFileToVault(gaiaUid);
        importedUids.push(imported.uid);
      } catch (error) {
        message.error(extractErrorDetail(error, 'Failed to import a GAIA file.'));
      }
    }
    if (importedUids.length) {
      await vaultStore.getVaultDirectory();
    }

    const vaultFileUids = [...new Set([...fileUids, ...uploadedUids, ...importedUids])];
    const resolvedUids = await Promise.all(vaultFileUids.map(resolveRevisionUid));
    const revisionUids = resolvedUids.filter((uid): uid is string => Boolean(uid));

    if (!revisionUids.length) {
      message.error('The selected files have no readable version to run.');
      return;
    }

    emit('confirm', revisionUids);
    close();
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to prepare the selected files.'));
  } finally {
    preparing.value = false;
  }
}

watch(
  () => props.show,
  isShown => {
    if (!isShown) resetState();
  }
);
</script>

<template>
  <n-modal
    v-model:show="showModal"
    preset="card"
    title="Start playbook run"
    :style="modalStyle"
    :mask-closable="!preparing"
    :closable="!preparing"
  >
    <FileSelector
      ref="fileSelectorRef"
      v-model:selected-keys="selectedFileKeys"
      :file-vault-file-only-selection="true"
      @tab-change="activeTab = $event"
      @upload-selection-change="handleUploadSelectionChange"
    />

    <template #footer>
      <n-space justify="end">
        <n-button :disabled="preparing" @click="close">Cancel</n-button>
        <n-button type="primary" :disabled="!canStart" :loading="preparing" @click="start">
          Start run
        </n-button>
      </n-space>
    </template>
  </n-modal>
</template>
